// Implements: F-IDN-027
/** One team in the Equipos grid (v3 `vTeams`): name, its people's avatars, size and leader. */

import type { ReactElement } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import { Avatar } from "../../../shared/ui/atoms/Avatar";
import { Icon } from "../../../shared/ui/atoms/Icon";
import type { TeamView } from "../../../types/organization";
import { isLead } from "../model/roles";
import styles from "./Organization.module.css";

export interface TeamCardProps {
  readonly team: TeamView;
  readonly href: string;
}

export function TeamCard({ team, href }: TeamCardProps): ReactElement {
  const { t } = useTranslation();
  const lead = team.members.find((member) => isLead(member.role));
  const size =
    team.members.length === 1
      ? t("organization.teams.oneMember")
      : t("organization.teams.members", { count: team.members.length });
  const leader = lead === undefined ? t("organization.teams.noLead") : t("organization.teams.lead", { name: lead.user.full_name });

  return (
    <Link to={href} className={styles.card}>
      <span className={styles.person}>
        <Icon name="users" size="sm" />
        <span className={styles.strong}>{team.name}</span>
      </span>
      {team.members.length === 0 ? null : (
        <span className={styles.avatars} aria-hidden="true">
          {team.members.map((member) => (
            <Avatar key={member.user.id} name={member.user.full_name} />
          ))}
        </span>
      )}
      <span className={styles.meta}>{`${size} · ${leader}`}</span>
    </Link>
  );
}
